<template>
  <div class="dashboard-panel">
    <!-- Skeleton loading -->
    <div v-if="!data" class="sla-skeleton">
      <div class="sla-skeleton__gauge" />
      <div class="sla-skeleton__bars">
        <div v-for="i in 3" :key="i" class="sla-skeleton__bar" />
      </div>
    </div>

    <!-- Content -->
    <div v-else class="sla-content" :class="{ 'sla-content--mobile': isMobile }">
      <!-- Gauge Chart (complianceRate) -->
      <div class="sla-gauge">
        <v-chart :option="gaugeOption" :style="gaugeStyle" autoresize />
      </div>

      <!-- Stats Cards -->
      <div class="sla-stats">
        <div class="sla-stat-card">
          <span class="sla-stat-card__label">{{ t('dashboard.totalCompleted') }}</span>
          <span class="sla-stat-card__value">{{ formatNumber(data.totalCompleted) }}</span>
        </div>
        <div class="sla-stat-card sla-stat-card--success">
          <span class="sla-stat-card__label">{{ t('dashboard.withinSla') }}</span>
          <span class="sla-stat-card__value">{{ formatNumber(data.withinSla) }}</span>
        </div>
        <div class="sla-stat-card sla-stat-card--danger">
          <span class="sla-stat-card__label">{{ t('dashboard.exceededSla') }}</span>
          <span class="sla-stat-card__value">{{ formatNumber(data.exceededSla) }}</span>
        </div>
        <div class="sla-stat-card sla-stat-card--info">
          <span class="sla-stat-card__label">{{ t('dashboard.avgBusinessHours') }}</span>
          <span class="sla-stat-card__value sla-stat-card__value--mono">
            {{ formatHours(data.averageBusinessHours) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps */
import { computed } from 'vue'
import i18n from '@/locales/i18n'
import { use } from 'echarts/core'
import { GaugeChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'
import { TooltipComponent } from 'echarts/components'
import VChart from 'vue-echarts'

use([CanvasRenderer, GaugeChart, TooltipComponent])

const t = i18n.global.t

const props = defineProps({
  data: { type: Object, default: null },
  isPcRedesign: { type: Boolean, default: false },
  isMobile: { type: Boolean, default: false }
})

const CHART_COLORS = {
  success: '#2E7D5B',
  warning: '#C8870A',
  danger: '#C03A3A'
}

const complianceRate = computed(() => {
  if (!props.data || props.data.complianceRate == null) return 0
  return Number(props.data.complianceRate)
})

function getGaugeColor(rate) {
  if (rate >= 90) return CHART_COLORS.success
  if (rate >= 70) return CHART_COLORS.warning
  return CHART_COLORS.danger
}

function formatNumber(val) {
  if (val == null) return '0'
  return Number(val).toLocaleString()
}

function formatHours(val) {
  if (val == null) return '-'
  return `${Number(val).toFixed(1)}h`
}

const gaugeOption = computed(() => ({
  series: [
    {
      type: 'gauge',
      startAngle: 220,
      endAngle: -40,
      min: 0,
      max: 100,
      pointer: { show: false },
      progress: {
        show: true,
        overlap: false,
        roundCap: true,
        clip: false,
        itemStyle: {
          color: getGaugeColor(complianceRate.value)
        }
      },
      axisLine: {
        lineStyle: {
          width: 16,
          color: [[1, '#E8EAED']]
        }
      },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      detail: {
        valueAnimation: true,
        fontSize: 32,
        fontWeight: 800,
        color: getGaugeColor(complianceRate.value),
        formatter: '{value}%',
        offsetCenter: [0, '10%']
      },
      title: {
        fontSize: 13,
        color: '#5C616B',
        offsetCenter: [0, '40%'],
        show: true
      },
      data: [
        {
          value: complianceRate.value,
          name: t('dashboard.slaActual')
        }
      ]
    }
  ]
}))

const gaugeStyle = computed(() => ({
  width: props.isMobile ? '180px' : '220px',
  height: props.isMobile ? '180px' : '220px'
}))
</script>

<style scoped>
/* ========================================
   SlaCompliancePanel
   ======================================== */
.dashboard-panel {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: var(--radius-xl, 16px);
  border: 1px solid var(--color-gray-100, #E8EAED);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.08));
  padding: 24px;
  transition: box-shadow var(--transition-normal, 0.2s ease), transform var(--transition-normal, 0.2s ease);
}

.dashboard-panel:hover {
  box-shadow: var(--shadow-md, 0 2px 6px rgba(0, 0, 0, 0.1));
  transform: translateY(-1px);
}

.dashboard-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-panel__title::before {
  content: '';
  width: 4px;
  height: 16px;
  background: var(--color-primary, #3B5998);
  border-radius: 2px;
  flex-shrink: 0;
}

/* ===== Content Layout ===== */
.sla-content {
  display: flex;
  align-items: center;
  gap: 24px;
}

.sla-content--mobile {
  flex-direction: column;
}

.sla-gauge {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

/* ===== Stats Cards ===== */
.sla-stats {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  min-width: 0;
}

.sla-stat-card {
  background: var(--color-gray-50, #F2F3F5);
  border-radius: var(--radius-lg, 12px);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid transparent;
}

.sla-stat-card--success {
  background: #ecfdf5;
  border-color: var(--color-success-light, #D4E9DF);
}

.sla-stat-card--danger {
  background: #fef2f2;
  border-color: var(--color-danger-light, #F2DCDC);
}

.sla-stat-card--info {
  background: #eff6ff;
  border-color: #C5D1E3;
}

.sla-stat-card__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-gray-500, #5C616B);
}

.sla-stat-card__value {
  font-size: 24px;
  font-weight: 800;
  color: var(--color-gray-800, #1E2228);
  line-height: 1;
}

.sla-stat-card--success .sla-stat-card__value {
  color: var(--color-success, #2E7D5B);
}

.sla-stat-card--danger .sla-stat-card__value {
  color: var(--color-danger, #C03A3A);
}

.sla-stat-card__value--mono {
  font-variant-numeric: tabular-nums;
  font-size: 20px;
}

/* ===== Skeleton ===== */
.sla-skeleton {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 0;
}

.sla-skeleton__gauge {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: linear-gradient(90deg, #E8EAED 25%, #F2F3F5 50%, #E8EAED 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  flex-shrink: 0;
}

.sla-skeleton__bars {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sla-skeleton__bar {
  height: 60px;
  border-radius: 8px;
  background: linear-gradient(90deg, #E8EAED 25%, #F2F3F5 50%, #E8EAED 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ===== Mobile ===== */
@media (max-width: 767px) {
  .dashboard-panel {
    padding: 16px;
    border-radius: var(--radius-lg, 12px);
  }
  .sla-skeleton {
    flex-direction: column;
  }
  .sla-stats {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .sla-stat-card {
    padding: 12px;
  }
  .sla-stat-card__value {
    font-size: 20px;
  }
}
</style>
